import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";

/**
 * EverReal Logo Component - Professional RWA Financial Design
 *
 * Modern logo component matching the professional financial aesthetic
 * with gradient containers, clean typography, and premium hover effects.
 *
 * Features:
 * - Multiple size variants (sm, md, lg)
 * - Multiple color variants (gold, emerald, gradient)
 * - Optional text display
 * - Professional hover effects and animations
 * - Accessible with keyboard navigation
 * - Fully type-safe
 */

export interface LogoProps {
  /**
   * Size of the logo
   * - sm: Small size for compact spaces (8px icon container)
   * - md: Medium size for headers (10px icon container) - default
   * - lg: Large size for hero sections (12px icon container)
   */
  size?: "sm" | "md" | "lg";

  /**
   * Whether to display brand text next to the icon
   * @default true
   */
  withText?: boolean;

  /**
   * Color variant
   * - gold: Professional gold gradient (primary)
   * - emerald: Brand emerald gradient
   * - gradient: Premium multi-color gradient
   * @default 'gold'
   */
  variant?: "gold" | "emerald" | "gradient";

  /**
   * Click handler for navigation
   */
  onClick?: () => void;

  /**
   * Use light colors for display on dark/colored backgrounds (e.g. blue header)
   * @default false
   */
  onDark?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

const sizeConfig = {
  sm: {
    container: "w-8 h-8",
    icon: "w-6 h-6",
    title: "text-base",
    subtitle: "text-[10px]",
  },
  md: {
    container: "w-10 h-10",
    icon: "w-8 h-8",
    title: "text-xl",
    subtitle: "text-xs",
  },
  lg: {
    container: "w-12 h-12",
    icon: "w-10 h-10",
    title: "text-2xl",
    subtitle: "text-sm",
  },
};

/**
 * Professional Logo Component
 */
export function Logo({
  size = "md",
  withText = true,
  variant = "gold",
  onDark = false,
  onClick,
  className,
}: LogoProps) {
  const { t } = useTranslation();
  const sizes = sizeConfig[size];
  const isClickable = !!onClick;

  // Unique gradient ID to avoid conflicts when multiple logos are on the same page
  const gradientId = `logoGradient-${variant}-${size}`;

  return (
    <div
      className={cn("flex items-center gap-3 group", className)}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-label={t("brand.logoLabel")}
    >
      {/* Icon Container with Transparent Background */}
      <div
        className={cn(
          sizes.container,
          "rounded-xl flex items-center justify-center transition-all group-hover:scale-110",
        )}
      >
        {/* BetMeBro Circle-B Brand SVG */}
        <svg viewBox="0 0 120 120" className={cn(sizes.icon)}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1e40af" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
          <circle
            cx="60"
            cy="60"
            r="50"
            fill={onDark ? "white" : `url(#${gradientId})`}
          />
          <path
            d="M 35 30 L 35 90 L 65 90 C 77 90 85 82 85 70 C 85 63 81 57 75 55 C 80 53 83 48 83 42 C 83 32 77 30 65 30 Z M 47 40 L 62 40 C 68 40 71 43 71 47 C 71 51 68 54 62 54 L 47 54 Z M 47 64 L 64 64 C 71 64 74 67 74 72 C 74 77 71 80 64 80 L 47 80 Z"
            fill={onDark ? "#1e40af" : "white"}
          />
          <circle cx="85" cy="35" r="8" fill="#f59e0b" />
        </svg>
      </div>

      {/* Brand Text */}
      {withText && (
        <div>
          <h1
            className={cn(
              sizes.title,
              "font-bold leading-tight",
              onDark ? "text-white" : "text-brand-primary",
            )}
          >
            {t("brand.name")}
          </h1>
          <p
            className={cn(
              sizes.subtitle,
              "leading-tight whitespace-nowrap",
              onDark ? "text-white/70" : "text-text-muted",
            )}
          >
            {t("brand.tagline")}
          </p>
        </div>
      )}
    </div>
  );
}
